import {
  getItemExpiryDate,
  getItemIssueDate,
  isAfterToday,
  isBeforeToday,
  daysUntil,
} from './brPure';

export const BR_STATUS = {
  PENDING: 'P',
  PENDING_MANUAL: 'PA',
  APPROVED: 'A',
  REJECTED: 'R',
  /** 历史兼容：旧 Header 可能为 C；现行取消走 Invalid（I） */
  CANCELLED: 'C',
  INVALID: 'I',
  READY: 'READY',
  NOT_YET_EFFECTIVE: 'NOT_YET_EFFECTIVE',
};

export const BR_STATUS_LABEL_KEY = {
  [BR_STATUS.NOT_YET_EFFECTIVE]: 'lbl_EMPBE_BR_Status_NotYetEffective',
  [BR_STATUS.APPROVED]: 'lbl_EMPBE_BR_Status_Approved',
  [BR_STATUS.READY]: 'lbl_EMPBE_BR_Status_Approved',
  [BR_STATUS.PENDING]: 'lbl_EMPBE_BR_Status_Pending',
  /* PA：供应商与管理端统一为「人工审批中」（更名 / 执照均只经人工） */
  [BR_STATUS.PENDING_MANUAL]: 'lbl_EMPBE_BR_Admin_Status_PendingManual',
  [BR_STATUS.REJECTED]: 'lbl_EMPBE_BR_Status_Rejected',
  [BR_STATUS.CANCELLED]: 'lbl_EMPBE_BR_Status_Cancelled',
  [BR_STATUS.INVALID]: 'lbl_EMPBE_BR_Status_Invalid',
};

export const BR_ADMIN_STATUS_LABEL_KEY = {
  [BR_STATUS.PENDING]: 'lbl_EMPBE_BR_Admin_Status_Pending',
  [BR_STATUS.PENDING_MANUAL]: 'lbl_EMPBE_BR_Admin_Status_PendingManual',
};

export const BR_EXPIRY_SOON_DAYS = 30;

export function getItemStatusCode(item) {
  return String(item?.statusCode || item?.StatusCode || '').toUpperCase();
}

/** 底层审批码是否为待审（P / PA）；不含显示态、有效期 */
export function isBrPendingCode(status) {
  const c = String(status || '').toUpperCase();
  return c === BR_STATUS.PENDING || c === BR_STATUS.PENDING_MANUAL;
}

/** 底层审批码是否为已通过（A）；不含 READY / 待生效等显示态 */
export function isBrApprovedCode(status) {
  const c = String(status || '').toUpperCase();
  return c === BR_STATUS.APPROVED;
}

function isBrItemNotYetEffective(item) {
  if (!item || !isBrApprovedCode(getItemStatusCode(item))) {
    return false;
  }
  const issue = getItemIssueDate(item);
  if (!issue) {
    return false;
  }
  return isAfterToday(issue);
}

function isBrItemNotExpired(item) {
  const expiry = getItemExpiryDate(item);
  if (!expiry) {
    return true;
  }
  return !isBeforeToday(expiry);
}

/** 是否已过期（有到期日且早于今天）；永久/无到期日 → false */
export function isBrItemExpired(item) {
  return !!item && !isBrItemNotExpired(item);
}

/**
 * 临期窗口（列表黄条 / 弹窗 / badge 共用）：
 * StatusCode=A、显示为已生效、有到期日且 0≤剩余天数≤30；当天 remaining=0 仍算临期。
 */
export function isBrExpiredSoon(item) {
  if (!item || !isBrApprovedCode(getItemStatusCode(item))) {
    return false;
  }
  if (item.isNameChangePending || item.IsNameChangePending) {
    return false;
  }
  if (isBrItemNotYetEffective(item) || !isBrItemNotExpired(item)) {
    return false;
  }
  const expiry = getItemExpiryDate(item);
  if (!expiry) {
    return false;
  }
  const remaining = daysUntil(expiry);
  return remaining >= 0 && remaining <= BR_EXPIRY_SOON_DAYS;
}

/** 显示层状态（审批码 + 日期 + 更名 pending 标记）；不是放行判定 */
export function resolveBrDisplayStatus(item) {
  if (!item) {
    return '';
  }
  const code = getItemStatusCode(item);
  if (item.isNameChangePending || item.IsNameChangePending) {
    return BR_STATUS.PENDING_MANUAL;
  }
  if (isBrApprovedCode(code)) {
    const expiry = getItemExpiryDate(item);
    if (expiry && isBeforeToday(expiry)) {
      return BR_STATUS.INVALID;
    }
    if (isBrItemNotYetEffective(item)) {
      return BR_STATUS.NOT_YET_EFFECTIVE;
    }
    return BR_STATUS.APPROVED;
  }
  if (code === BR_STATUS.INVALID || code === 'INVALID') {
    return BR_STATUS.INVALID;
  }
  return code;
}

export function resolveBrBadgeStatus(item) {
  if (!item) {
    return '';
  }
  if (item.isSupersededByNameChange || item.IsSupersededByNameChange) {
    return BR_STATUS.INVALID;
  }
  const displayStatus = resolveBrDisplayStatus(item);
  if (displayStatus === BR_STATUS.PENDING_MANUAL) {
    return BR_STATUS.PENDING_MANUAL;
  }
  if (displayStatus === BR_STATUS.APPROVED && !isBrExpiredSoon(item)) {
    return BR_STATUS.READY;
  }
  return displayStatus;
}

export function resolveBrUiStatus(item) {
  return resolveBrBadgeStatus(item) || getItemStatusCode(item);
}

export function resolveBrStatusLabelKey(statusCode) {
  const code = String(statusCode || '').toUpperCase();
  return BR_STATUS_LABEL_KEY[code] || BR_STATUS_LABEL_KEY[BR_STATUS.APPROVED];
}

/** 管理端狀態文案：P→审批中，PA→人工审批中；其餘回落供應商映射 */
export function resolveBrAdminStatusLabelKey(statusCode) {
  const code = String(statusCode || '').toUpperCase();
  if (code === BR_STATUS.PENDING) {
    return BR_ADMIN_STATUS_LABEL_KEY[BR_STATUS.PENDING];
  }
  if (code === BR_STATUS.PENDING_MANUAL) {
    return BR_ADMIN_STATUS_LABEL_KEY[BR_STATUS.PENDING_MANUAL];
  }
  return resolveBrStatusLabelKey(code);
}

export function isBrItemInEffectivePeriod(item) {
  if (!item || !isBrApprovedCode(getItemStatusCode(item))) {
    return false;
  }
  if (isBrItemNotYetEffective(item)) {
    return false;
  }
  return isBrItemNotExpired(item);
}

/** 本地放行条件：A + 已生效 + 未过期（与列表主导照挑选无关） */
export function isBrVerifiedItem(item) {
  return isBrItemInEffectivePeriod(item);
}
